import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/glyph/add';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ui_softwareReleasesVersionRelatedWork_RelatedWork$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionRelatedWork_RelatedWork.graphql';
import type { ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery.graphql';
import { useAddRelatedWorkExperienceFail } from '@atlassian/jira-software-releases-add-related-work-to-version/src/services/experience-tracker/add-related-work/index.tsx';
import { AddRelatedWorkPopup } from '@atlassian/jira-software-releases-add-related-work-to-version/src/ui/add-related-work-popup/async.tsx';
import {
	CollapsibleSection,
	CollapsibleToggleButton,
} from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { ErrorBoundary } from '../common/utils/error-boundary.tsx';
import RelatedWorkAnalyticsContext from './analytics-context/index.tsx';
import { CategoryFilter } from './category-filter/index.tsx';
import { RelatedWorkEmptyState } from './empty-state/index.tsx';
import messages from './messages.tsx';
import { RelatedWorkList } from './related-work-list/async.tsx';

export const RELATED_WORK_SHOW_MORE_THRESHOLD = 5;

type Props = {
	queryRef: PreloadedQuery<ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery>;
};

export const RelatedWork = ({ queryRef }: Props) => {
	const { formatMessage } = useIntl();

	const { jira } = usePreloadedQuery<ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery>(
		graphql`
			query ui_softwareReleasesVersionRelatedWork_RelatedWorkQuery(
				$versionAri: ID!
				$isDevopsFeatureDisabledInFedRamp: Boolean!
			) {
				jira @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						...ui_softwareReleasesVersionRelatedWork_RelatedWork
							@skip(if: $isDevopsFeatureDisabledInFedRamp)
					}
				}
			}
		`,
		queryRef,
	);
	const versionFrag: ui_softwareReleasesVersionRelatedWork_RelatedWork$key = jira.version;

	const version = useFragment(
		graphql`
			fragment ui_softwareReleasesVersionRelatedWork_RelatedWork on JiraVersion {
				relatedWorkV2(first: 100)
					@connection(key: "version_relatedWorkV2")
					@required(action: THROW) {
					edges @required(action: THROW) {
						__typename
						...relatedWorkList_softwareReleasesVersionRelatedWork_relatedWorkEdges
						...categoryFilter_softwareReleasesVersionRelatedWork
						...async_softwareReleasesAddRelatedWorkToVersion_AddRelatedWorkPopup_relatedWorkEdges
						...emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_relatedWorkEdges
						...analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext
						node {
							...async_softwareReleasesAddRelatedWorkToVersion_EditRelatedWorkPopup_jiraVersionRelatedWork
							category
						}
					}
				}
				status
				...async_softwareReleasesAddRelatedWorkToVersion_AddRelatedWorkPopup_jiraVersion
				...async_softwareReleasesAddRelatedWorkToVersion_EditRelatedWorkPopup_jiraVersion
				...emptyState_softwareReleasesVersionRelatedWork_RelatedWorkEmptyState_jiraVersion
				...relatedWorkList_softwareReleasesVersionRelatedWork_jiraVersion
				...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
			}
		`,
		versionFrag,
	);

	const [selectedCategory, setSelectedCategory] = useQueryParam('relatedWorkCategory');
	const addRelatedWorkExpFailed = useAddRelatedWorkExperienceFail();

	const allRelatedWork = version.relatedWorkV2.edges.filter(Boolean);

	const relatedWorkInCategory =
		selectedCategory === undefined
			? allRelatedWork
			: allRelatedWork.filter(({ node }) => node?.category === selectedCategory);

	const body =
		allRelatedWork.length === 0 ? (
			<RelatedWorkEmptyState jiraVersion={version} relatedWorkEdges={allRelatedWork} />
		) : (
			<>
				<ErrorBoundary id="relatedWorkCategoryFilter" fallback="flag">
					<CategoryFilter
						relatedWorkEdges={allRelatedWork}
						selectedCategory={selectedCategory}
						onCategorySelected={(category) => setSelectedCategory(category, 'replace')}
					/>
				</ErrorBoundary>
				<RelatedWorkList jiraVersion={version} relatedWorkEdges={relatedWorkInCategory} />
				<ActionsContainer>
					{version.status != null && version.status !== 'ARCHIVED' && (
						<ErrorBoundary
							id="addRelatedPopup"
							fallback="flag"
							onError={(location: string, error: Error) => {
								addRelatedWorkExpFailed(location, error);
							}}
						>
							<AddRelatedWorkPopup
								jiraVersion={version}
								relatedWorkEdges={allRelatedWork}
								renderTrigger={({ triggerProps, isLoading, onClick, onMouseOver }) => (
									<AddButtonContainer>
										<AddButton
											{...triggerProps}
											appearance="subtle-link"
											testId={
												isLoading
													? undefined
													: 'software-releases-version-related-work.ui.add-button'
											}
											iconBefore={
												<ActionIconContainer>
													<AddIcon size="small" label="" />
												</ActionIconContainer>
											}
											onClick={onClick}
											onMouseOver={onMouseOver}
											shouldFitContainer
										>
											<ShowMoreTextContainer>
												{formatMessage(messages.addRelatedWork)}
											</ShowMoreTextContainer>
										</AddButton>
									</AddButtonContainer>
								)}
							/>
						</ErrorBoundary>
					)}
				</ActionsContainer>
			</>
		);

	return (
		<RelatedWorkAnalyticsContext relatedWorkEdges={allRelatedWork}>
			<FireScreenAnalytics />
			<CollapsibleSection
				jiraVersion={version}
				uiType="RELATED_WORK"
				renderHeader={(isCollapsed, onToggleClicked) => (
					<HeaderWrapper>
						<HeaderLeftWrapper onClick={onToggleClicked}>
							<Heading size="small" as="h2">
								<Box xcss={sectionTitleStyles}>{formatMessage(messages.relatedWorkHeading)}</Box>
							</Heading>
							<TotalCountLozenge>{allRelatedWork.length}</TotalCountLozenge>
							<CollapsibleToggleButton isCollapsed={isCollapsed} />
						</HeaderLeftWrapper>
						<HeaderRightWrapper>
							{version.status !== 'ARCHIVED' && (
								<ErrorBoundary
									key="addRelatedWorkButton"
									id="addRelatedPopupHeaderPlusButton"
									fallback="flag"
									onError={(location: string, error: Error) => {
										addRelatedWorkExpFailed(location, error);
									}}
								>
									<AddRelatedWorkPopup
										jiraVersion={version}
										relatedWorkEdges={allRelatedWork}
										renderTrigger={({ triggerProps, onClick, onMouseOver }) => (
											<AddIconButtonContainer>
												<Button
													{...triggerProps}
													appearance="subtle"
													iconBefore={<AddIcon label="" size="small" />}
													onClick={onClick}
													onMouseOver={onMouseOver}
													shouldFitContainer
													aria-label={formatMessage(messages.addRelatedWork)}
												/>
											</AddIconButtonContainer>
										)}
									/>
								</ErrorBoundary>
							)}
						</HeaderRightWrapper>
					</HeaderWrapper>
				)}
				renderBody={() => body}
			/>
		</RelatedWorkAnalyticsContext>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: token('space.200', '16px'),
	paddingTop: token('space.100', '8px'),
	paddingLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionIconContainer = styled.span({
	paddingRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShowMoreTextContainer = styled.span({
	fontWeight: token('font.weight.regular'),
});

// RLSS-2812 color.background.accent.gray.subtlest might be better than color.background.neutral

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddButtonContainer = styled.div({
	width: '100%',
	borderRadius: '5px',
	cursor: 'pointer',
	paddingTop: token('space.100', '8px'),
	paddingRight: 0,
	paddingBottom: token('space.100', '8px'),
	paddingLeft: 0,
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: `${token('color.background.neutral', colors.N20A)}`,
	},
	'&:focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: `${token('color.background.neutral', colors.N20A)}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': { textAlign: 'left' },
});

const sectionTitleStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.300',
	marginRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddIconButtonContainer = styled.div({
	width: token('space.400', '32px'),
	height: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginTop: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLeftWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderRightWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TotalCountLozenge = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N40),

	font: token('font.body.small'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize}px`,
	// This margin is to align vertically, due to font style, 2 pixels are misaligned that looks not right.
	marginTop: token('space.025', '2px'),
	paddingRight: token('space.050', '4px'),
	paddingLeft: token('space.050', '4px'),
});
